import React, { useEffect, useState } from 'react';
import { Button, Field, Header, Input, Message, Modal, Toast, Table as FrontierTable } from 'react-frontier';
import { EmploymentIncidenceSummary, EmploymentSchedule, EmploymentScheduleType, SetLoading, UserEmployment } from '../SiteClasses';
import { addCommas, bindFormChange, getEmploymentLogTypeName, getEmploymentLogTypeOptions } from '../SiteUtil';
import { Dropdown, Table as SemanticTable } from 'semantic-ui-react';
import moment from 'moment';
import API from '../API';
import Validator from '../Validator';

interface EmploymentIncidenceForm{
	log_type_id: number;
	date_start: number;
	date_end: number;
	notes: string;
}

interface EmploymentIncidenceModalProps{
	open: boolean;
	onClose: ()=>void;
	employment_id: number;
	user: UserEmployment;
	assignedSchedules: EmploymentSchedule[];
	loadingSchedules: boolean;
	dayNames: string[];
	onCreated: (incidences: EmploymentIncidenceSummary[], count: number)=>void;
	onDeleted: (log_id: number)=>void;
}

var EmploymentIncidenceModal = (props: EmploymentIncidenceModalProps)=>{
	var [form, setForm] = useState<EmploymentIncidenceForm>({
		log_type_id: null,
		date_start: null,
		date_end: null,
		notes: '',
	});
	var [prompts, setPrompts] = useState<string[]>([]);

	useEffect(()=>{
		if(!props.open) return;
		setForm({
			log_type_id: null,
			date_start: null,
			date_end: null,
			notes: '',
		});
		setPrompts([]);
	}, [props.open]);

	var onFormChange = bindFormChange(form, setForm);

	var todayStartUnix = moment().startOf('day').unix();
	var date_start_unix = form.date_start ? moment.unix(form.date_start).startOf('day').unix() : null;
	var date_end_unix = form.date_end ? moment.unix(form.date_end).endOf('day').unix() : null;
	var hasIncidenceDates = !!(form.log_type_id && form.date_start && form.date_end);
	var isPastRange = hasIncidenceDates && date_end_unix < todayStartUnix;
	var isProgrammedRange = hasIncidenceDates && date_start_unix >= todayStartUnix;
	var isMixedRange = hasIncidenceDates && !isPastRange && !isProgrammedRange;
	var matchedSchedules = !isProgrammedRange || form.date_end < form.date_start ? [] : props.assignedSchedules.filter(schedule => {
		if(schedule.type_id !== EmploymentScheduleType.WORK) return false;
		var rangeStart = moment.unix(form.date_start).startOf('day');
		var rangeEnd = moment.unix(form.date_end).endOf('day');
		for(var currentDate = rangeStart.clone(); currentDate.isSameOrBefore(rangeEnd, 'day'); currentDate.add(1, 'day')){
			if(schedule.day === currentDate.day()) return true;
		}
		return false;
	});
	var createIncidences = (setLoading: SetLoading)=>{
		if(!props.user) return setPrompts(['Selecciona un usuario']);
		var { valid, prompts } = Validator(form, {
			log_type_id: [{ rule: 'number', prompt: 'Selecciona un tipo de incidencia' }],
			date_start: [{ rule: 'number', prompt: 'Selecciona una fecha de inicio válida' }],
			date_end: [{ rule: 'number', prompt: 'Selecciona una fecha de fin válida' }],
		});
		setPrompts(prompts);
		if(!valid) return;
		if(date_end_unix < date_start_unix) return setPrompts(['La fecha de fin no puede ser menor a la fecha de inicio']);
		if(isMixedRange) return setPrompts(['Las incidencias pasadas y programadas se deben registrar por separado']);
		if(isProgrammedRange && matchedSchedules.length === 0) return setPrompts(['No hay horarios afectados']);
		var schedule_ids = isPastRange ? (props.assignedSchedules.filter(schedule => schedule.type_id === EmploymentScheduleType.WORK).map(schedule => schedule.schedule_id)) : matchedSchedules.map(schedule => schedule.schedule_id);

		setLoading(true);
		API.createEmploymentIncidences(props.employment_id, props.user.user_id, schedule_ids, form.log_type_id, date_start_unix, date_end_unix, form.notes).then(res=>{
			if(res.error) return setPrompts([res.message]);
			var incidences = ((res.data?.incidences || []) as EmploymentIncidenceSummary[]).sort((a, b)=> a.date_start - b.date_start);
			Toast.success(`Se registraron ${res.data.count} incidencia(s) para ${props.user.name}`);
			props.onCreated(incidences, res.data.count);
		}).catch(()=>{
			setPrompts(['Hubo un error guardando la incidencia (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var deleteIncidence = (log_id: number)=>{
		API.deleteEmploymentIncidence(log_id).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Incidencia eliminada');
			props.onDeleted(log_id);
		}).catch(()=>{
			setPrompts(['Hubo un error eliminando la incidencia (LCL-1)']);
		});
	};

		return <Modal open={props.open} onClose={props.onClose} size='tiny'>
			<Modal.Header>Incidencia</Modal.Header>
			<Modal.Content style={{ padding: '0px 0px 10px 0px' }}>
				<FrontierTable
					details
					striped
					style={{ borderRadius: 0 }}
					data={props.user && [
						['ID de usuario', props.user.user_id],
						['Nombre', props.user.name],
						['Correo electrónico', props.user.email]
					]}
				/>
				<div style={{ padding: '15px 10px 0px 10px' }}>
					<Field label='Tipo de incidencia'>
						<Dropdown
							fluid
							selection
							placeholder='Selecciona un tipo'
							options={getEmploymentLogTypeOptions()}
							value={form.log_type_id}
							onChange={onFormChange('log_type_id', true)}
						/>
					</Field>
					<Field amount={2}>
						<Input
							label='Fecha de inicio'
							value={form.date_start}
							onChange={onFormChange('date_start')}
							calendar={{
								date: form.date_start || moment().startOf('day').unix(),
								mode: 'date',
								format: 'DD/MM/YYYY',
							}}
							style={{ width: '100%' }}
						/>
						<Input
							label='Fecha de fin'
							value={form.date_end}
							onChange={onFormChange('date_end')}
							calendar={{
								date: form.date_end || moment().endOf('day').unix(),
								mode: 'date',
								format: 'DD/MM/YYYY',
							}}
							style={{ width: '100%' }}
						/>
					</Field>
					<Field label='Comentarios'>
						<Input
							value={form.notes}
							onChange={onFormChange('notes')}
							placeholder='Opcional'
							textarea
						/>
					</Field>
				</div>
				{hasIncidenceDates && !isMixedRange && !isPastRange && (
					props.loadingSchedules ? (
						<div style={{ marginTop: 15 }}>
							<Header loading text='Cargando horarios...' />
						</div>
					) : matchedSchedules.length > 0 ? (
						<Field label='Horarios afectados' style={{ marginBottom: 0, marginLeft: 5 }}>
							<SemanticTable celled striped fixed className='ar incidence-schedule-preview' style={{ marginTop: 0, width: '100%' }}>
								<SemanticTable.Body>
									{matchedSchedules.map(schedule => (
										<SemanticTable.Row key={schedule.schedule_id} className='schedule-row selected'>
											<SemanticTable.Cell><strong>{props.dayNames[schedule.day]}</strong></SemanticTable.Cell>
											<SemanticTable.Cell>{moment().startOf('day').add(schedule.time_start, 'minutes').format('HH:mm')} - {moment().startOf('day').add(schedule.time_end, 'minutes').format('HH:mm')}</SemanticTable.Cell>
											<SemanticTable.Cell>${addCommas(schedule.salary)}</SemanticTable.Cell>
										</SemanticTable.Row>
									))}
								</SemanticTable.Body>
							</SemanticTable>
						</Field>
					) : props.assignedSchedules.length > 0 ? (
						<div style={{ marginTop: 15, padding: '12px 0', textAlign: 'center', color: '#666', fontSize: 16 }}>
							No hay horarios afectados
						</div>
					) : (
						<div style={{ marginTop: 15, padding: '12px 0', textAlign: 'center', color: '#666', fontSize: 16 }}>
							No hay horarios asignados
						</div>
					)
				)}
				{props.user?.incidences?.length > 0 ? (<>
					<Field label='Incidencias registradas' style={{ marginBottom: 0, marginLeft: 5 }} />
						<SemanticTable celled striped compact className='ar incidence-summary-preview' style={{ marginTop: 0, width: '100%' }}>
							<SemanticTable.Body>
								{props.user.incidences.map(log => {
									var formattedDate = moment.unix(log.date_start).format('DD/MM/YYYY');
									var incidenceInfo = `${formattedDate} • ${log.log_type_name || getEmploymentLogTypeName(log.log_type_id)}`;
									return <SemanticTable.Row key={log.log_id || `${log.schedule_id}-${log.date_start}`} className='summary-row'>
										<SemanticTable.Cell width={2}><strong>{props.dayNames[log.day]}</strong></SemanticTable.Cell>
										<SemanticTable.Cell width={6} title={incidenceInfo}>
											<div className='summary-info-text'>{incidenceInfo}</div>
										</SemanticTable.Cell>
										<SemanticTable.Cell className='summary-comments-cell'>{log.notes || '---'}</SemanticTable.Cell>
										<SemanticTable.Cell collapsing textAlign='center'>
											<Button icon basic iconName='trash' size='tiny' onClick={()=> deleteIncidence(log.log_id)} />
										</SemanticTable.Cell>
									</SemanticTable.Row>;
								})}
							</SemanticTable.Body>
						</SemanticTable>
					</>
				) : null}
			<Message type="error" list={prompts} style={{ marginTop: 15 }} />
		</Modal.Content>
		<Modal.Actions>
			<Button text='Cancelar' basic onClick={props.onClose} />
			<Button text='Guardar' color='black' onClick={createIncidences} disabled={isMixedRange || (isProgrammedRange && matchedSchedules.length === 0)} />
		</Modal.Actions>
	</Modal>;
}

export default EmploymentIncidenceModal;
